/**
 * Shared chat utility functions — no React, no Firestore, pure functions.
 */

/** Deterministic conversation ID for 1:1 DMs */
export function convoId(uid1, uid2) { return [uid1, uid2].sort().join('_'); }

/** Firestore Timestamp | Date | epoch → epoch millis; 0 when absent or invalid.
 *  RW-1688: read receipts order one participant's read mark against another's
 *  message, so they need a comparable number, not a formatted string. A
 *  serverTimestamp() that hasn't resolved yet reads as null — 0 keeps that
 *  "not yet" rather than "the beginning of time". */
export function tsMillis(ts) {
  if (!ts) return 0;
  const d = ts.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
  const ms = d.getTime();
  return isNaN(ms) ? 0 : ms;
}

/** RW-1688: should the "Seen" receipt render under the last message?
 *
 *  True only when the newest message is mine, it has a resolved timestamp, and
 *  the other participant's read mark reaches it. Deliberately NOT a function of
 *  unreadCount: that counter goes back up when I send again or when the
 *  onNewDirectMessage trigger lands late, neither of which un-sees a message
 *  they already read. 1:1 only — group receipts need per-reader state. */
export function seenReceiptVisible(convo, messages, myUid, outboxLength = 0) {
  if (!convo || convo.type === 'group' || outboxLength > 0) return false;
  if (!Array.isArray(messages) || messages.length === 0 || !myUid) return false;
  const last = messages[messages.length - 1];
  if (last.senderId !== myUid) return false;
  const otherUid = (convo.participants || []).find((p) => p !== myUid);
  if (!otherUid) return false;
  const sentAt = tsMillis(last.timestamp);
  if (!sentAt) return false;
  return tsMillis(convo.lastReadAt?.[otherUid]) >= sentAt;
}

/** Relative time formatting */
export function fmtTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000 && d.getDate() === now.getDate()) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diff < 172800000) return 'Yesterday';
  if (diff < 604800000) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Full timestamp for message hover */
export function fmtFullTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Date divider text */
export function fmtDateDivider(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = today - msgDay;
  if (diff === 0) return 'Today';
  if (diff === 86400000) return 'Yesterday';
  if (diff < 604800000) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

/** Date key for grouping messages by day */
export function getDateKey(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Is user online (last seen within 5 minutes) */
export function isOnline(lastSeen) {
  if (!lastSeen) return false;
  const d = lastSeen.toDate ? lastSeen.toDate() : lastSeen instanceof Date ? lastSeen : new Date(lastSeen);
  return Date.now() - d.getTime() < 300000;
}

/** Last active text */
export function lastActiveText(lastSeen) {
  if (!lastSeen) return 'Offline';
  if (isOnline(lastSeen)) return 'Active now';
  const d = lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen);
  const diff = Date.now() - d.getTime();
  if (diff < 3600000) return `Active ${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `Active ${Math.floor(diff / 3600000)}h ago`;
  return `Active ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
}

/* RW-2531: the attachment predicates moved to lib/attachments — posts,
 * comments and chat now share ONE definition of "is this an image / video /
 * previewable". Re-exported here so every existing chat import still works;
 * do not reimplement any of them beside this line. */
export { fmtSize, isImageType, isVideoType, canPreviewImage, getFileIcon } from '../../lib/attachments';

/** Parse rich text: *bold*, _italic_, ~strikethrough~, `code` */
export function formatRichText(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/`([^`]+)`/g, '<code style="background:#E5E7EB;padding:1px 4px;border-radius:3px;font-size:13px">$1</code>')
    .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/~([^~]+)~/g, '<del style="color:#9CA3AF">$1</del>');
}

/** Extract URLs from text */
export function extractUrls(text) {
  if (!text) return [];
  const regex = /https?:\/\/[^\s<>"')\]]+/gi;
  return text.match(regex) || [];
}

/**
 * Grapheme segmenter for isEmojiOnly, built once on first use (RW-1992).
 * Intl.Segmenter loads ICU segmentation data, so building one per call \u2014 during
 * render, for every short message in the thread \u2014 is the wrong ratio.
 * Deliberately lazy rather than a module-level const: a bare const would move
 * the throw on an environment without Intl.Segmenter from "a short message
 * rendered" to "the chat chunk was imported", and module-level consts in
 * widely-imported modules are the CRA scope-hoist/TDZ pattern (Gotcha #11).
 */
let graphemeSegmenter;
function getGraphemeSegmenter() {
  if (!graphemeSegmenter) graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  return graphemeSegmenter;
}

/** Check if text is only emoji (1-3 chars) */
export function isEmojiOnly(text) {
  if (!text) return false;
  const emojiRegex = /^(?:\p{Emoji_Presentation}|\p{Extended_Pictographic})(?:\uFE0F)?(?:\u200D(?:\p{Emoji_Presentation}|\p{Extended_Pictographic})(?:\uFE0F)?)*$/u;
  const cleaned = text.trim();
  if (cleaned.length > 12) return false;
  const segments = [...getGraphemeSegmenter().segment(cleaned)];
  return segments.length >= 1 && segments.length <= 3 && segments.every((s) => emojiRegex.test(s.segment));
}

/** Truncate text */
export function truncate(text, maxLength = 60) {
  if (!text || text.length <= maxLength) return text || '';
  return text.slice(0, maxLength) + '...';
}

/** Get conversation display info */
export function getConvoDisplay(convo, currentUid, allUsers) {
  if (convo.type === 'group') {
    return { name: convo.groupName || 'Group', avatar: convo.groupAvatar || '', isGroup: true, memberCount: (convo.participants || []).length };
  }
  const otherUid = (convo.participants || []).find((p) => p !== currentUid) || '';
  const details = convo.participantDetails?.[otherUid];
  const name = details?.name || convo.participantNames?.[otherUid] || 'Member';
  const avatar = details?.avatar || convo.participantPhotos?.[otherUid] || '';
  const otherUser = (allUsers || []).find((u) => u.uid === otherUid);
  return { name, avatar, isGroup: false, online: otherUser ? isOnline(otherUser.lastSeen) : false, otherUser, otherUid };
}

/** Generate a temp client ID */
/** RW-3513: has anyone sent anything here? Opening someone's profile → Message
 *  creates the conversation doc up front, so the list filled with "No messages
 *  yet" rows for conversations that never happened. Any send stamps the
 *  preview and its sender — the client (lastMessage / lastMessageBy) and the
 *  server trigger (lastMessage / lastSenderId) alike. */
export function hasFirstMessage(convo) {
  return !!(convo && (convo.lastMessage || convo.lastMessageBy || convo.lastSenderId));
}

export function genClientId() { return `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; }
